import { financePalette } from './chunk-AEFY5JL4.mjs';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';

var menuPaper = {
  backgroundColor: "rgba(255, 255, 255, 0.92)",
  backdropFilter: "blur(24px)",
  WebkitBackdropFilter: "blur(24px)"
};
var lightGlassmorphismTheme = createTheme({
  palette: {
    mode: "light",
    finance: financePalette,
    background: {
      default: "transparent",
      paper: "rgba(255, 255, 255, 0.15)",
      // Fully opaque surface for the playing list, which slides up over the
      // player controls and must hide them completely. Menus use a 0.92 glass
      // tint because they float over a page, but this panel covers same-card
      // content, so it goes solid (the opaque form of the menu hue). Typed in
      // `./domainPalette`.
      overlay: "rgb(255, 255, 255)"
    },
    primary: {
      main: "#667eea",
      light: "#8b9feb",
      dark: "#4c63d2",
      contrastText: "#ffffff"
    },
    secondary: {
      main: "#764ba2",
      light: "#9a7ab8",
      dark: "#5a3a7c",
      contrastText: "#ffffff"
    },
    text: {
      primary: "rgba(0, 0, 0, 0.87)",
      secondary: "rgba(0, 0, 0, 0.6)",
      disabled: "rgba(0, 0, 0, 0.38)"
    },
    divider: "rgba(0, 0, 0, 0.1)"
  },
  shape: {
    borderRadius: 8
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.5)",
          boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.1)"
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
          color: "rgba(0, 0, 0, 0.87)",
          transition: "all 0.3s ease",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
            transform: "translateY(-2px)"
          }
        },
        contained: {
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          border: "none",
          color: "#ffffff",
          "&:hover": {
            background: "linear-gradient(135deg, #7b91f0 0%, #8a6ab8 100%)"
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          // Mirror the light Paper treatment (white tint, light border and
          // shadow); keep the Card's stronger blur so it stays a touch more
          // solid than a plain Paper, matching the dark Card↔Paper relation.
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          border: "1px solid rgba(255, 255, 255, 0.5)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)"
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
          boxShadow: "0 4px 30px rgba(0, 0, 0, 0.05)"
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          // Opaque enough to stay legible: at low opacity the backdrop scrim
          // bleeds through and content reads as muddy grey (worst as a
          // fullscreen modal on mobile).
          backgroundColor: "rgba(255, 255, 255, 0.92)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          border: "1px solid rgba(255, 255, 255, 0.5)",
          boxShadow: "0 16px 64px rgba(0, 0, 0, 0.2)"
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: menuPaper
      }
    },
    MuiPopover: {
      styleOverrides: {
        paper: menuPaper
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            borderRadius: 12,
            "& fieldset": {
              borderColor: "rgba(0, 0, 0, 0.1)"
            },
            "&:hover fieldset": {
              borderColor: "rgba(0, 0, 0, 0.2)"
            },
            "&.Mui-focused fieldset": {
              borderColor: "#667eea"
            }
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(0, 0, 0, 0.03)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.1)"
          }
        },
        filled: {
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          border: "none",
          color: "#ffffff"
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: "#f8f9fa",
          minHeight: "100vh"
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "rgba(0, 0, 0, 0.6)",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.05)"
          }
        }
      }
    }
  }
});
var theme = responsiveFontSizes(lightGlassmorphismTheme);

export { theme };
//# sourceMappingURL=chunk-D4M7VTYQ.mjs.map
//# sourceMappingURL=chunk-D4M7VTYQ.mjs.map